/**
 * La scheda «Resi e sostituzioni» (Davide 02/11/2026): resi e sostituzioni
 * come costo, e il dossier Itamoby/Ityhome per chiedere imballi migliori e uno
 * sconto. Tutto dalle fatture: dei fornitori (pezzi), dei corrieri (etichette).
 */

import { Link } from "react-router-dom";
import type { BiCostiPostVendita, BiDettagli, BiDossierFornitore, BiDossierTrend } from "../biApi";
import { eur, intero, n, nomeFornitore, perc } from "../biTesti";
import { InfoTip } from "./InfoTip";
import { Riquadro } from "./Riquadro";

/** «1 su 7»: un tasso detto come lo direbbe Davide al fornitore. */
export function unoSu(rate: number | null): string {
  if (rate == null || rate <= 0) return "—";
  return `1 su ${Math.max(1, Math.round(100 / rate))}`;
}

// ── Il costo del periodo ─────────────────────────────────────────────────────

export function CostoPostVendita({ c, d }: { c: BiCostiPostVendita; d: BiDettagli }) {
  const margine = n(d.economia.margin);
  const dopo = margine - n(c.total);
  const mangiato = margine > 0 ? (n(c.total) / margine) * 100 : null;
  const voci = [
    {
      nome: "Sostituzioni",
      valore: eur(c.replacements_total),
      sotto: `${intero(c.replacement_orders)} ordini`,
      spiegazione: {
        titolo: "Sostituzioni",
        testo:
          "Ordini del periodo con un pezzo rimandato. Il pezzo costa quanto scritto nella fattura di ricambio di Itamoby o Ityhome (rif. «SOST»), l'etichetta quanto la fattura del corriere. Senza IVA.",
        conto: [
          {
            etichetta: `Pezzi (${intero(c.parts_invoices)} fatture)`,
            valore: eur(c.parts_cost, 2),
          },
          {
            segno: "+" as const,
            etichetta: "AllService scritto a mano ÷ 1,22",
            valore: eur(c.manual_cost, 2),
          },
          {
            segno: "+" as const,
            etichetta: "Etichette di sostituzione",
            valore: eur(c.replacement_labels_cost, 2),
          },
          { segno: "=" as const, etichetta: "Costo", valore: eur(c.replacements_total, 2) },
        ],
      },
    },
    {
      nome: "Resi",
      valore: eur(c.returns_total),
      sotto: `${intero(c.return_orders)} ordini`,
      spiegazione: {
        titolo: "Resi",
        testo:
          "Un reso ci costa la spedizione di andata, che nessuno ci ridà, e l'etichetta di ritorno. Il fatturato e il costo della merce sono gia' fuori dai conti. Il rimborso del fornitore si vede qui sotto ma non si toglie: la merce non era nel margine.",
        conto: [
          {
            etichetta: "Spedizione di andata persa",
            valore: eur(c.return_outbound_cost, 2),
          },
          {
            segno: "+" as const,
            etichetta: "Etichette di ritorno",
            valore: eur(c.return_labels_cost, 2),
          },
          { segno: "=" as const, etichetta: "Costo", valore: eur(c.returns_total, 2) },
        ],
        incerto:
          n(c.return_no_shipping) > 0
            ? `${intero(c.return_no_shipping)} resi senza costo di spedizione: contano zero.`
            : undefined,
      },
    },
    {
      nome: "Rimborsato dai fornitori",
      valore: eur(c.return_supplier_refund),
      sotto: "sui resi del periodo",
      spiegazione: {
        titolo: "Rimborsato dai fornitori",
        testo:
          "Le note di credito di Itamoby e Ityhome sui resi del periodo («per merce resa dal cliente»), senza IVA. E' la merce che ci ridanno: non entra nel costo qui sopra.",
      },
    },
  ];
  return (
    <section
      aria-label="Quanto costano resi e sostituzioni"
      className="bi-entra relative mb-4 overflow-hidden rounded-[22px] bg-rail px-7 py-6 text-white max-md:rounded-[18px] max-md:px-4 max-md:py-5"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-28 -top-36 h-[320px] w-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(201,100,66,0.32),transparent)] blur-2xl"
      />
      <div className="relative grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-10 max-lg:grid-cols-1 max-lg:gap-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-[14px] font-bold">Resi e sostituzioni ci sono costati</h2>
            <InfoTip
              spiegazione={{
                titolo: "Costo di resi e sostituzioni",
                testo:
                  "Sugli ordini del periodo e dei filtri (data ordine): pezzi di ricambio, etichette e spedizioni perse. E' una spesa che il margine netto non conta.",
                conto: [
                  { etichetta: "Sostituzioni", valore: eur(c.replacements_total, 2) },
                  { segno: "+", etichetta: "Resi", valore: eur(c.returns_total, 2) },
                  { segno: "=", etichetta: "Totale", valore: eur(c.total, 2) },
                ],
              }}
            />
          </div>
          <p className="mt-2 font-display text-[clamp(2.4rem,4.2vw,4rem)] font-bold leading-[0.95] tracking-[-0.035em] tabular-nums">
            {eur(c.total)}
          </p>
          <p className="mt-3 text-[14px] font-semibold">
            {mangiato == null ? (
              "Nessun margine nel periodo."
            ) : (
              <>
                Si mangiano il <b className="font-display text-[20px]">{perc(mangiato)}</b> del
                margine.
              </>
            )}
          </p>
          <p className="mt-3 flex flex-wrap items-center gap-2 text-[13px]">
            Margine {eur(margine)} → dopo resi e sostituzioni <b>{eur(dopo)}</b>
            <InfoTip
              spiegazione={{
                titolo: "Margine dopo resi e sostituzioni",
                testo:
                  "Il margine netto degli ordini calcolabili meno quello che sono costati resi e sostituzioni degli ordini del periodo.",
                conto: [
                  { etichetta: "Margine netto", valore: eur(margine, 2) },
                  { segno: "−", etichetta: "Resi e sostituzioni", valore: eur(c.total, 2) },
                  { segno: "=", etichetta: "Ti restano", valore: eur(dopo, 2) },
                ],
              }}
            />
          </p>
        </div>
        <ul className="grid grid-cols-3 gap-3 self-end max-sm:grid-cols-1">
          {voci.map((v) => (
            <li key={v.nome} className="min-w-0 rounded-[14px] bg-white/[0.07] px-4 py-3.5">
              <span className="flex items-center justify-between gap-2 text-[12.5px] font-bold">
                {v.nome}
                <InfoTip spiegazione={v.spiegazione} />
              </span>
              <span className="mt-1.5 block font-display text-[24px] font-bold leading-none tabular-nums">
                {v.valore}
              </span>
              <span className="mt-1.5 block text-[12px] font-semibold">{v.sotto}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ── Itamoby e Ityhome a confronto ────────────────────────────────────────────

function SchedaFornitore({ f, altro }: { f: BiDossierFornitore; altro?: BiDossierFornitore }) {
  const massimo = Math.max(1, ...f.years.map((y) => n(y.rate)));
  const rimborsi = f.credit_notes.find((c) => c.reason === "reso");
  const righe: [string, string][] = [
    ["Ordini comprati", intero(f.orders)],
    ["Con un ricambio", intero(f.replaced_orders)],
    ["Pezzi pagati", eur(f.parts_cost)],
    ["Etichette nostre", eur(f.labels_cost)],
    ["Pezzi mandati gratis", intero(f.free_parts)],
    ["Imballo rinforzato sui ricambi", `${intero(f.reinforced_packaging)} volte`],
    [
      "Dall'ordine al ricambio",
      f.median_days == null ? "—" : `${intero(f.median_days)} giorni (metà dei casi)`,
    ],
    [
      "Note di credito per reso",
      rimborsi ? `${intero(rimborsi.count)} · ${eur(rimborsi.amount)}` : "—",
    ],
  ];
  return (
    <article className="min-w-0 rounded-[16px] bg-paper p-4">
      <header className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-[20px] font-bold">{nomeFornitore(f.supplier)}</h3>
        <span className="text-[12px] font-semibold">dal 2024, dalle fatture</span>
      </header>
      <p className="mt-3 font-display text-[44px] font-bold leading-none tracking-[-0.03em] tabular-nums">
        {unoSu(f.rate)}
      </p>
      <p className="mt-1.5 text-[13px] font-semibold">
        ordini torna con un pezzo rotto o mancante · {perc(f.rate)}
        {altro && altro.rate != null && f.rate != null && f.rate > altro.rate && (
          <span className="ml-1.5 inline-flex h-[20px] items-center rounded-full bg-red-soft px-2 text-[11.5px] font-bold">
            {(f.rate / altro.rate).toLocaleString("it-IT", { maximumFractionDigits: 1 })}×{" "}
            {nomeFornitore(altro.supplier)}
          </span>
        )}
      </p>
      <ul className="mt-4 flex items-end gap-2" aria-label="Tasso per anno">
        {f.years
          .filter((y) => n(y.orders) >= 20)
          .map((y) => (
            <li key={y.year} className="flex min-w-0 flex-1 flex-col items-center gap-1">
              <span className="text-[12px] font-bold tabular-nums">{perc(y.rate)}</span>
              <span className="flex h-[54px] w-full items-end rounded-[8px] bg-paper-2">
                <span
                  className="bi-cresce block w-full rounded-[8px] bg-[#c96442]"
                  style={{ height: `${(n(y.rate) / massimo) * 100}%` }}
                />
              </span>
              <span className="text-[11.5px] font-semibold tabular-nums">
                {y.year} · {intero(y.orders)} ord.
              </span>
            </li>
          ))}
      </ul>
      <dl className="mt-4 divide-y divide-line text-[13px]">
        {righe.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3 py-1.5">
            <dt className="font-semibold">{k}</dt>
            <dd className="text-right font-bold tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

export function DossierFornitori({ t }: { t: BiDossierTrend }) {
  const [a, b] = t.fornitori;
  return (
    <Riquadro
      titolo="Itamoby e Ityhome"
      spiegazione={{
        titolo: "Itamoby e Ityhome",
        testo:
          "Da tutte le loro fatture elettroniche dal 2024 (archivio Eureka): ogni ordine comprato e ogni fattura di ricambio («SOST», «COMPL», «INTEGR»), ricondotta all'ordine d'origine. Vale per tutti i canali e tutto lo storico: periodo e filtri qui non si applicano.",
        conto: [
          { etichetta: "Tasso", valore: "ordini con ricambio ÷ ordini comprati" },
          { etichetta: "Anno", valore: "quello dell'ordine d'origine" },
        ],
        incerto:
          "Un ricambio senza fattura d'origine conta nel costo, non nel tasso. L'anno in corso non e' finito: alcuni ricambi devono ancora arrivare.",
      }}
    >
      <div className="grid grid-cols-2 gap-3 max-lg:grid-cols-1">
        {a && <SchedaFornitore f={a} altro={b} />}
        {b && <SchedaFornitore f={b} altro={a} />}
      </div>
    </Riquadro>
  );
}

// ── Cosa si rompe ────────────────────────────────────────────────────────────

export function ComponentiRotti({ t }: { t: BiDossierTrend }) {
  const totale = t.componenti.reduce((s, c) => s + n(c.pieces), 0);
  const massimo = Math.max(1, ...t.componenti.map((c) => n(c.pieces)));
  return (
    <Riquadro
      titolo="I pezzi che si rompono di più"
      spiegazione={{
        titolo: "I pezzi che si rompono di più",
        testo:
          "Ogni riga delle fatture di ricambio Itamoby e Ityhome, raggruppata per tipo di pezzo dal suo nome («COLLO FONDI PER LETTO 160 X 190» = fondi del contenitore). Imballi e bancali non contano.",
        conto: [
          { etichetta: "Pezzi", valore: "quantita' in fattura" },
          { etichetta: "Gratis", valore: "righe a 0 €: il fornitore se l'e' preso lui" },
        ],
      }}
    >
      <ol className="flex flex-col gap-3">
        {t.componenti.slice(0, 10).map((c, i) => (
          <li key={c.part} className="text-[13px]">
            <span className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate">
                <span className="mr-2 font-bold tabular-nums">{i + 1}</span>
                <span className="font-bold">{c.part}</span>
                <span className="ml-2 text-[12px] font-semibold">
                  {c.suppliers.map(nomeFornitore).join(", ")}
                </span>
              </span>
              <span className="shrink-0 font-bold tabular-nums">
                {intero(c.pieces)} pz
                <span className="ml-1.5 text-[12px] font-semibold opacity-70">
                  {perc((n(c.pieces) / Math.max(1, totale)) * 100, 0)}
                </span>
              </span>
            </span>
            <span className="mt-1 block h-[7px] overflow-hidden rounded-full bg-paper-2">
              <span
                className="bi-cresce block h-full rounded-full bg-ink"
                style={{ width: `${(n(c.pieces) / massimo) * 100}%` }}
              />
            </span>
            <span className="mt-1 block text-[12px] font-semibold tabular-nums">
              {eur(c.cost)} · {intero(c.orders)} ordini
              {n(c.free) > 0 && ` · ${intero(c.free)} gratis`}
            </span>
          </li>
        ))}
      </ol>
    </Riquadro>
  );
}

export function ModelliProblemi({ t }: { t: BiDossierTrend }) {
  return (
    <Riquadro
      titolo="I modelli con più ricambi"
      spiegazione={{
        titolo: "I modelli con più ricambi",
        testo:
          "Il modello del mobile comprato (dal nome in fattura: ANKEL, NUAMO, DEMAS...), quante volte e' tornato un pezzo e quale pezzo si rompe di piu' su quel modello.",
        conto: [{ etichetta: "Tasso", valore: "ordini con ricambio ÷ ordini comprati" }],
        incerto: "Sotto i 20 ordini comprati il tasso e' poco affidabile: e' scritto in chiaro.",
      }}
    >
      <div className="overflow-x-auto max-md:-mx-4 max-md:px-4">
        <table className="w-full min-w-[560px] text-[13px]">
          <thead>
            <tr className="border-b border-line text-left text-[12px] font-bold">
              <th className="py-2 pr-3">Modello</th>
              <th className="py-2 pr-3 text-right">Comprati</th>
              <th className="py-2 pr-3 text-right">Con ricambio</th>
              <th className="py-2 pr-3">Tasso</th>
              <th className="py-2 pr-3 text-right">Pezzi</th>
              <th className="py-2">Si rompe di più</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {t.modelli.slice(0, 12).map((m) => (
              <tr key={`${m.supplier}-${m.model}`}>
                <td className="py-2 pr-3">
                  <span className="block font-bold">{m.model}</span>
                  <span className="block text-[12px] font-semibold">
                    {nomeFornitore(m.supplier)}
                  </span>
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">{intero(m.orders)}</td>
                <td className="py-2 pr-3 text-right font-bold tabular-nums">
                  {intero(m.replaced_orders)}
                </td>
                <td className="py-2 pr-3">
                  <span className="flex items-center gap-2">
                    <span className="h-[7px] w-[64px] overflow-hidden rounded-full bg-paper-2">
                      <span
                        className="block h-full rounded-full bg-[#c96442]"
                        style={{ width: `${Math.min(100, n(m.rate) * 3)}%` }}
                      />
                    </span>
                    <span className="font-bold tabular-nums">{perc(m.rate, 0)}</span>
                    {n(m.orders) < 20 && (
                      <span className="rounded-full bg-amber-soft px-1.5 text-[11px] font-bold">
                        pochi
                      </span>
                    )}
                  </span>
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">{eur(m.cost)}</td>
                <td className="py-2 font-semibold">{m.top_part ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Riquadro>
  );
}

// ── Cosa dire al fornitore ───────────────────────────────────────────────────

/** Gli argomenti per la trattativa, scritti dai numeri veri: niente frasi fisse. */
export function argomenti(t: BiDossierTrend): string[] {
  const out: string[] = [];
  const [a, b] = t.fornitori;
  const peggiore = [a, b]
    .filter((f): f is BiDossierFornitore => f != null && f.rate != null)
    .sort((x, y) => n(y.rate) - n(x.rate))[0];
  const migliore = [a, b].find((f) => f && f !== peggiore && f.rate != null);
  if (peggiore) {
    out.push(
      `${nomeFornitore(peggiore.supplier)}: ${unoSu(peggiore.rate)} ordini torna con un ricambio (${perc(peggiore.rate)} su ${intero(peggiore.orders)} ordini).` +
        (migliore ? ` ${nomeFornitore(migliore.supplier)} si ferma a ${perc(migliore.rate)}.` : ""),
    );
    const anni = peggiore.years.filter((y) => n(y.orders) >= 20);
    const primo = anni[0];
    const ultimo = anni[anni.length - 1];
    if (primo && ultimo && primo !== ultimo && n(ultimo.rate) > n(primo.rate)) {
      out.push(
        `E sta peggiorando: ${perc(primo.rate)} nel ${primo.year}, ${perc(ultimo.rate)} nel ${ultimo.year}.`,
      );
    }
  }
  const tutti = [a, b].filter((f): f is BiDossierFornitore => f != null);
  const entro = [peggiore, ...tutti].find((f) => f != null && n(f.within_30_days) >= 60);
  if (entro) {
    out.push(
      `Il ${perc(entro.within_30_days, 0)} dei ricambi ${nomeFornitore(entro.supplier)} parte entro 30 giorni dall'ordine: il pezzo arriva rotto o si rompe al montaggio, non con l'uso.`,
    );
  }
  const pezzi = t.componenti.reduce((s, c) => s + n(c.pieces), 0);
  const lunghi = t.componenti
    .filter((c) => c.part.startsWith("Fondi") || c.part.startsWith("Fascioni"))
    .reduce((s, c) => s + n(c.pieces), 0);
  if (pezzi > 0 && lunghi / pezzi >= 0.2) {
    out.push(
      `Fondi del contenitore e fascioni sono il ${perc((lunghi / pezzi) * 100, 0)} dei pezzi rimandati: pannelli lunghi e sottili, l'imballo di oggi non li protegge.`,
    );
  }
  const imballi = tutti.reduce((s, f) => s + n(f.reinforced_packaging), 0);
  if (imballi > 0) {
    out.push(
      `Sui ricambi mettono gia' l'«imballo rinforzato» (${intero(imballi)} volte): chiediamo lo stesso imballo sull'ordine normale.`,
    );
  }
  const costo = tutti.reduce((s, f) => s + n(f.parts_cost) + n(f.labels_cost), 0);
  if (costo > 0) {
    out.push(
      `Ci sono costati ${eur(costo)} fra pezzi ed etichette, senza contare il tempo, i clienti scontenti e le recensioni.`,
    );
  }
  return out;
}

export function DaDireAlFornitore({ t }: { t: BiDossierTrend }) {
  const righe = argomenti(t);
  return (
    <Riquadro
      titolo="Da dire al fornitore"
      spiegazione={{
        titolo: "Da dire al fornitore",
        testo:
          "Gli argomenti per chiedere imballi migliori e uno sconto, scritti dai numeri qui accanto. Cambiano da soli quando arrivano fatture nuove.",
      }}
    >
      {righe.length === 0 ? (
        <p className="text-[13px]">Ancora pochi dati.</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {righe.map((r, i) => (
            <li
              key={r}
              className="grid grid-cols-[26px_minmax(0,1fr)] gap-2 text-[14px] leading-snug"
            >
              <span className="grid h-[22px] w-[22px] place-items-center rounded-full bg-ink text-[12px] font-bold text-on-ink">
                {i + 1}
              </span>
              <span className="font-semibold">{r}</span>
            </li>
          ))}
        </ol>
      )}
    </Riquadro>
  );
}

export function UltimiRicambi({ t, quanti = 12 }: { t: BiDossierTrend; quanti?: number }) {
  return (
    <Riquadro
      titolo="Ultimi ricambi"
      spiegazione={{
        titolo: "Ultimi ricambi",
        testo: "Le ultime righe delle fatture di ricambio, dalla piu' recente. Senza IVA.",
      }}
    >
      <ol className="divide-y divide-line">
        {t.ultimi.slice(0, quanti).map((u, i) => (
          <li
            key={`${u.date}-${u.part}-${i}`}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-2 text-[13px]"
          >
            <span className="min-w-0">
              <span className="block truncate font-bold">{u.part}</span>
              <span className="block text-[12px] font-semibold">
                {nomeFornitore(u.supplier)} · {u.date.split("-").reverse().join("/")}
                {u.order_reference && (
                  <>
                    {" · "}
                    <Link
                      to={`/ordini/${encodeURIComponent(u.order_reference)}`}
                      className="underline underline-offset-2"
                    >
                      {u.order_reference}
                    </Link>
                  </>
                )}
              </span>
            </span>
            <span className="font-bold tabular-nums">
              {n(u.cost) === 0 ? "gratis" : eur(u.cost, 2)}
            </span>
          </li>
        ))}
      </ol>
    </Riquadro>
  );
}
